import React, { useEffect, useRef, useState } from "react";
import useTranslation from "../utils/i18n/useTranslation";
import "react-date-picker/dist/DatePicker.css";
import "react-calendar/dist/Calendar.css";
import styles from "./Home.module.scss";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { Carousel } from "react-responsive-carousel";
import { motion, AnimatePresence } from "framer-motion";
import { FcMoneyTransfer } from "react-icons/fc";
import { FaMoneyBillTransfer, FaHandHoldingHeart } from "react-icons/fa6";
import { GrGallery, GrMoney } from "react-icons/gr";
import moment from "moment";
import {
  getSumFromCollection,
  getTopNFromCollection,
  getPosts,
  getEvents,
  getGalleryEntry,
  getVisibilitySettings,
  DEFAULT_VISIBILITY,
} from "../utils/network/Crud";
import useTheme from "../utils/useTheme";
import { toast } from "react-toastify";
import { ThreeDots } from "react-loader-spinner";
import { GiExpense, GiLotus } from "react-icons/gi";
import { LiaDonateSolid } from "react-icons/lia";
import { useNavigate } from "react-router-dom";
import { TbBuildingCommunity } from "react-icons/tb";
import { BsRecord2Fill } from "react-icons/bs";
import {
  MdOutlineEvent,
  MdOutlineCampaign,
  MdClose,
  MdSearch,
} from "react-icons/md";
import { GiTempleGate } from "react-icons/gi";
import LiveStream from "./LiveStream";
import { useMemo } from "react";
import CircularProgressBar from "../Components/ProgressBar/CircularProgressBar";
import { toneFor, initialOf } from "../utils/avatar";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const staggerContainer = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
};

// Every section starts hidden until the real settings load from Firestore,
// instead of flashing every section (DEFAULT_VISIBILITY) and then hiding some.
const HIDDEN_VISIBILITY = Object.fromEntries(
  Object.keys(DEFAULT_VISIBILITY).map((key) => [key, false]),
);

const AnimatedNumber = ({ value = 0 }) => {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    const to = Number(value) || 0;
    if (!to) {
      setDisplay(0);
      return;
    }
    const start = performance.now();
    const duration = 1100;
    let frame;
    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(to * eased));
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [value]);
  return <>{display.toLocaleString()}</>;
};

// Feeds the pointer position to the card's CSS spotlight (--mx / --my).
const trackSpotlight = (e) => {
  const rect = e.currentTarget.getBoundingClientRect();
  e.currentTarget.style.setProperty("--mx", `${e.clientX - rect.left}px`);
  e.currentTarget.style.setProperty("--my", `${e.clientY - rect.top}px`);
};

const DONORS_COLLAPSED_COUNT = 7;

const SectionHeading = ({ icon, children }) => (
  <span className={styles.sectionHeading}>
    {icon}
    {children}
  </span>
);

const Home = () => {
  const [homeData, setHomeData] = useState({
    totalDonation: "",
    totalExpenses: "",
    topDonars: [],
  });
  const [posts, setPosts] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [visibility, setVisibility] = useState(HIDDEN_VISIBILITY);
  const [expandedPost, setExpandedPost] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const { toggleGlobalLoading, globalLoading, configs, currentYear } =
    useTheme();
  const { t } = useTranslation();
  const [donorQuery, setDonorQuery] = useState("");
  const [showAllDonors, setShowAllDonors] = useState(false);
  const amountLeft = homeData?.totalDonation - homeData?.totalExpenses;
  const totalDonation = Number(homeData?.totalDonation) || 0;
  const totalExpenses = Number(homeData?.totalExpenses) || 0;
  const spentPct =
    totalDonation > 0
      ? Math.min(100, Math.round((totalExpenses / totalDonation) * 100))
      : 0;

  const donors = homeData?.topDonars || [];
  const normalizedQuery = donorQuery.trim().toLowerCase();
  const matchingDonors = donors.filter(
    (donor) =>
      !normalizedQuery ||
      String(donor?.name || "")
        .toLowerCase()
        .includes(normalizedQuery),
  );
  const canCollapseDonors =
    !normalizedQuery && matchingDonors.length > DONORS_COLLAPSED_COUNT;
  const visibleDonors =
    canCollapseDonors && !showAllDonors
      ? matchingDonors.slice(0, DONORS_COLLAPSED_COUNT)
      : matchingDonors;
  const liveRef = useRef(null);
  const navigate = useNavigate();
  const homeRequestRef = useRef(0);

  const heroImages = [
    require("../assets/img_1.jpg"),
    require("../assets/img_2.jpg"),
    require("../assets/img_3.jpg"),
    require("../assets/img_4.jpg"),
    require("../assets/img_5.jpg"),
    require("../assets/img_6.jpg"),
    require("../assets/img_7.jpg"),
    require("../assets/img_8.jpg"),
    require("../assets/img_9.jpg"),
    require("../assets/img_10.jpg"),
    require("../assets/img_11.jpg"),
  ];

  const getHomeData = async () => {
    const requestId = ++homeRequestRef.current;
    try {
      const [donationsSum, expensesSum, top10Donations] = await Promise.all([
        getSumFromCollection("donation", "amount", currentYear),
        getSumFromCollection("expense", "amount", currentYear),
        // Infinity: list every donation, not just the first 50 (same cached read)
        getTopNFromCollection("donation", "amount", Infinity, currentYear),
      ]);
      if (requestId !== homeRequestRef.current) return; // a newer request already landed
      setHomeData((prev) => ({
        ...prev,
        totalDonation: donationsSum,
        totalExpenses: expensesSum,
        topDonars: top10Donations,
      }));
    } catch (error) {
      if (requestId !== homeRequestRef.current) return;
      toast.error(t("somethingWentWrong"), { autoClose: 1200 });
    }
  };
  useEffect(() => {
    getHomeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentYear]);

  useEffect(() => {
    const loadExtras = async () => {
      try {
        const [postsRes, eventsRes, visibilityRes, galleryRes] =
          await Promise.all([
            getPosts(),
            getEvents(),
            getVisibilitySettings(),
            getGalleryEntry(),
          ]);
        setPosts(postsRes?.data?.slice(0, 4) || []);
        const now = new Date();
        const upcoming = (eventsRes?.data || [])
          .filter((e) => {
            const d = e.date?.toDate ? e.date.toDate() : e.date;
            return d && d >= now;
          })
          .slice(0, 3);
        setUpcomingEvents(upcoming);
        setVisibility(visibilityRes);
        setGalleryImages(
          [...(galleryRes?.data?.images || [])].reverse().slice(0, 10),
        );
      } catch (error) {
        // Fail open: an unexpected error shouldn't leave every section hidden forever.
        setVisibility(DEFAULT_VISIBILITY);
      }
    };
    loadExtras();
  }, []);

  const isLive = visibility.showLiveStream && configs?.showLive;
  const nextEvent = upcomingEvents[0];
  const nextEventDate = nextEvent?.date?.toDate
    ? nextEvent.date.toDate()
    : nextEvent?.date;
  const daysToNextEvent = nextEventDate
    ? Math.max(
        0,
        moment(nextEventDate)
          .startOf("day")
          .diff(moment().startOf("day"), "days"),
      )
    : null;

  const scrollToLive = () => {
    liveRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const shuffledHeroImages = useMemo(() => {
    return [...heroImages].sort(() => Math.random() - 0.5).slice(0, 6);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const petals = useMemo(
    () =>
      Array.from({ length: 10 }, (_, i) => ({
        id: i,
        left: Math.round(Math.random() * 96) + 2,
        delay: Math.round(Math.random() * 9 * 10) / 10,
        duration: 9 + Math.round(Math.random() * 6),
        size: 10 + Math.round(Math.random() * 8),
        symbol: i % 3 === 0 ? "✦" : i % 3 === 1 ? "❀" : "●",
      })),
    [],
  );
  return (
    <div className={styles.container}>
      <section className={styles.hero}>
        <div className={styles.heroBackdrop}>
          {shuffledHeroImages.map((src, i) => (
            <img
              key={i}
              src={src}
              alt=""
              className={styles.heroBgImg}
              style={{ animationDelay: `${i * 4}s, 0s` }}
            />
          ))}
          <div className={styles.heroOverlay} />
        </div>
        <div className={styles.heroParticles} aria-hidden="true">
          {petals.map((p) => (
            <span
              key={p.id}
              className={styles.heroParticle}
              style={{
                left: `${p.left}%`,
                animationDelay: `${p.delay}s`,
                animationDuration: `${p.duration}s`,
                fontSize: `${p.size}px`,
              }}
            >
              {p.symbol}
            </span>
          ))}
        </div>
        <div className={styles.heroOrnament}>ॐ</div>
        <motion.div
          className={styles.heroContent}
          initial="hidden"
          animate="show"
          variants={staggerContainer}
        >
          <motion.div variants={fadeUp} className={styles.heroBadge}>
            <GiLotus /> जय माता दी
          </motion.div>
          <motion.h1 variants={fadeUp}>
            {t("heroTitlePrefix")} <span>{t("heroTitleSuffix")}</span>
          </motion.h1>
          <motion.p variants={fadeUp} className={styles.heroSubtitle}>
            {t("heroSubtitle")}
          </motion.p>
          {isLive && (
            <motion.div variants={fadeUp} className={styles.heroCtas}>
              <button className={styles.heroPrimaryBtn} onClick={scrollToLive}>
                <BsRecord2Fill color="#ff4444" /> {t("watchLive")}
              </button>
            </motion.div>
          )}
        </motion.div>
      </section>

      {visibility.showEvents && nextEvent && daysToNextEvent !== null && (
        <motion.section
          className={styles.countdownBanner}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeUp}
          onClick={() => navigate("events")}
        >
          <GiTempleGate className={styles.countdownIcon} />
          <div className={styles.countdownText}>
            <span className={styles.countdownLabel}>
              {daysToNextEvent === 0
                ? t("happeningToday")
                : t("nextCelebration")}
            </span>
            <h3>{nextEvent.title}</h3>
          </div>
          <div className={styles.countdownNumber}>
            <strong>
              <AnimatedNumber value={daysToNextEvent} />
            </strong>
            <span>{daysToNextEvent === 1 ? t("day") : t("days")}</span>
          </div>
        </motion.section>
      )}

      <motion.section
        className={styles.mainTop}
        initial="hidden"
        animate="show"
        variants={staggerContainer}
      >
        {visibility.showDonations && (
          <motion.div
            variants={fadeUp}
            whileHover={{ y: -5 }}
            whileTap={{ scale: 0.94, rotate: -2 }}
            className={styles.itemCard}
            onClick={() => navigate("donations")}
          >
            <div className={styles.logoCard}>
              <LiaDonateSolid className={styles.logo} />
            </div>
            <span className={styles.itemTitle}>{t("daanpeti")}</span>
          </motion.div>
        )}
        {visibility.showExpenses && (
          <motion.div
            variants={fadeUp}
            whileHover={{ y: -5 }}
            whileTap={{ scale: 0.94, rotate: -2 }}
            className={`${styles.itemCard} ${styles.accentRose}`}
            onClick={() => navigate("expenses")}
          >
            <div className={styles.logoCard}>
              <GiExpense className={styles.logo} />
            </div>
            <span className={styles.itemTitle}>{t("expense")}</span>
          </motion.div>
        )}
        {visibility.showGallery && (
          <motion.div
            variants={fadeUp}
            whileHover={{ y: -5 }}
            whileTap={{ scale: 0.94, rotate: -2 }}
            className={`${styles.itemCard} ${styles.accentViolet}`}
            onClick={() => navigate("gallery")}
          >
            <div className={styles.logoCard}>
              <GrGallery className={styles.logo} />
            </div>
            <span className={styles.itemTitle}>{t("gallery")}</span>
          </motion.div>
        )}
        {visibility.showCommunity && (
          <motion.div
            variants={fadeUp}
            whileHover={{ y: -5 }}
            whileTap={{ scale: 0.94, rotate: -2 }}
            className={`${styles.itemCard} ${styles.accentTeal}`}
            onClick={() => navigate("community")}
          >
            <div className={styles.logoCard}>
              <TbBuildingCommunity className={styles.logo} />
            </div>
            <span className={styles.itemTitle}>{t("community")}</span>
          </motion.div>
        )}
        {visibility.showEvents && (
          <motion.div
            variants={fadeUp}
            whileHover={{ y: -5 }}
            whileTap={{ scale: 0.94, rotate: -2 }}
            className={`${styles.itemCard} ${styles.accentSky}`}
            onClick={() => navigate("events")}
          >
            <div className={styles.logoCard}>
              <MdOutlineEvent className={styles.logo} />
            </div>
            <span className={styles.itemTitle}>{t("events")}</span>
          </motion.div>
        )}
      </motion.section>

      {isLive && (
        <motion.section
          ref={liveRef}
          className={styles.eventsSection}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          variants={fadeUp}
        >
          <SectionHeading icon={<BsRecord2Fill color="red" />}>
            {t("liveDarshan")}
          </SectionHeading>
          <LiveStream
            channelId={configs?.channelId}
            videoId={configs?.liveVideoId}
          />
        </motion.section>
      )}

      {visibility.showPosts && posts.length > 0 && (
        <motion.section
          className={styles.eventsSection}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          variants={fadeUp}
        >
          <SectionHeading icon={<MdOutlineCampaign />}>
            {t("announcements")}
          </SectionHeading>
          <motion.div
            className={styles.postsList}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
            variants={staggerContainer}
          >
            {posts.map((post) => (
              <motion.div
                className={styles.postCard}
                key={post.id}
                variants={fadeUp}
                whileTap={{ scale: 0.98 }}
                onClick={() => setExpandedPost(post)}
                role="button"
                tabIndex={0}
                title={t("tapToReadMore")}
              >
                {post.image && <img src={post.image} alt={post.title} />}
                <div>
                  <h4>{post.title}</h4>
                  <p>{post.content}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.section>
      )}

      {visibility.showEvents && upcomingEvents.length > 0 && (
        <motion.section
          className={styles.eventsSection}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          variants={fadeUp}
        >
          <SectionHeading icon={<MdOutlineEvent />}>
            {t("upcomingEvents")}
          </SectionHeading>
          <motion.div
            className={styles.upcomingList}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
            variants={staggerContainer}
          >
            {upcomingEvents.map((event) => {
              const dateVal = event.date?.toDate
                ? event.date.toDate()
                : event.date;
              return (
                <motion.div
                  className={styles.upcomingCard}
                  key={event.id}
                  variants={fadeUp}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => navigate("events")}
                >
                  {dateVal && (
                    <div className={styles.dateBadge}>
                      <span>{moment(dateVal).format("DD")}</span>
                      <small>{moment(dateVal).format("MMM")}</small>
                    </div>
                  )}
                  <div>
                    <h4>{event.title}</h4>
                    {event.location && <p>{event.location}</p>}
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </motion.section>
      )}

      {globalLoading ? (
        <div className={styles.loaderContainer}>
          <ThreeDots
            visible={true}
            height="80"
            width="80"
            color="rgb(251,157,71)"
            radius="9"
            ariaLabel="three-dots-loading"
            wrapperStyle={{}}
            wrapperClass=""
          />
        </div>
      ) : (
        <>
          {(visibility.showDonations || visibility.showExpenses) && (
            <motion.section
              className={styles.summarySection}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.2 }}
              variants={staggerContainer}
            >
              {visibility.showDonations && (
                <motion.div
                  variants={fadeUp}
                  whileTap={{ scale: 0.97 }}
                  onPointerMove={trackSpotlight}
                  className={`${styles.card} ${styles.donarCard}`}
                >
                  <div className={styles.cardTop}>
                    <span className={styles.iconWrap}>
                      <FcMoneyTransfer className={styles.icon} />
                    </span>
                    <span className={styles.yearChip}>{currentYear}</span>
                  </div>
                  <h2>{t("totalDonations")}</h2>
                  <p>
                    ₹ <AnimatedNumber value={homeData?.totalDonation || 0} />
                  </p>
                </motion.div>
              )}
              {visibility.showExpenses && (
                <motion.div
                  variants={fadeUp}
                  whileTap={{ scale: 0.97 }}
                  onPointerMove={trackSpotlight}
                  className={`${styles.card} ${styles.expenseCard}`}
                >
                  <div className={styles.cardTop}>
                    <span className={styles.iconWrap}>
                      <FaMoneyBillTransfer className={styles.icon} />
                    </span>
                    <span className={styles.yearChip}>{currentYear}</span>
                  </div>
                  <h2>{t("totalExpenses")}</h2>
                  <p>
                    ₹ <AnimatedNumber value={homeData?.totalExpenses || 0} />
                  </p>
                </motion.div>
              )}
            </motion.section>
          )}
          {visibility.showDonations && visibility.showExpenses && (
            <motion.section
              className={`${styles.amountLeftSection} ${
                amountLeft < 0 ? styles.overspent : ""
              }`}
              initial="hidden"
              animate="show"
              variants={fadeUp}
              whileTap={{ scale: 0.99 }}
              onPointerMove={trackSpotlight}
            >
              <div className={styles.amountLeftMain}>
                <span className={styles.iconWrap}>
                  <GrMoney className={styles.icon} />
                </span>
                <h2>{t("amountLeft")}</h2>
                <p>
                  ₹ <AnimatedNumber value={amountLeft || 0} />
                </p>
                {totalDonation > 0 && (
                  <span className={styles.spentNote}>
                    {spentPct}% {t("fundsUsed")}
                  </span>
                )}
              </div>
              {totalDonation > 0 && (
                <div className={styles.utilizationRing}>
                  <CircularProgressBar
                    key={currentYear}
                    name={t("utilised")}
                    percentage={spentPct}
                  />
                </div>
              )}
            </motion.section>
          )}
          {visibility.showDonations && donors.length > 0 && (
            <motion.section
              className={styles.donorsSection}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.2 }}
              variants={fadeUp}
            >
              <h2>
                <FaHandHoldingHeart className={styles.headingIcon} />{" "}
                {t("donorsTitle")}
              </h2>

              {donors.length > 3 && (
                <div className={styles.donorSearch}>
                  <MdSearch />
                  <input
                    type="search"
                    value={donorQuery}
                    onChange={(e) => setDonorQuery(e.target.value)}
                    placeholder={t("searchPlaceholder")}
                    aria-label={t("donorsTitle")}
                  />
                </div>
              )}

              {visibleDonors.length > 0 ? (
                // Rows animate themselves (not via parent variants): rows added
                // later by "Show all" / search never receive the parent's "show"
                // state and would stay invisible while still blocking taps.
                <ul>
                  {visibleDonors.map((donor, index) => {
                    const [toneA, toneB] = toneFor(donor?.name);
                    return (
                      <motion.li
                        key={donor.id ?? `${donor?.name}-${index}`}
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          duration: 0.35,
                          ease: "easeOut",
                          delay: Math.min(index, 8) * 0.04,
                        }}
                        whileHover={{ y: -2 }}
                        whileTap={{ scale: 0.98 }}
                        style={{ "--tone-a": toneA, "--tone-b": toneB }}
                      >
                        <span className={styles.donorAvatar} aria-hidden="true">
                          {initialOf(donor?.name)}
                        </span>
                        <span className={styles.donorName}>{donor?.name}</span>
                        <strong>
                          ₹ {(Number(donor?.amount) || 0).toLocaleString()}
                        </strong>
                      </motion.li>
                    );
                  })}
                </ul>
              ) : (
                <p className={styles.donorEmpty}>{t("noDonorMatch")}</p>
              )}

              {canCollapseDonors && (
                <button
                  type="button"
                  className={styles.donorMore}
                  onClick={() => setShowAllDonors((prev) => !prev)}
                >
                  {showAllDonors
                    ? t("showLess")
                    : `${t("showAllDonors")} (${matchingDonors.length})`}
                </button>
              )}
            </motion.section>
          )}
        </>
      )}

      <AnimatePresence>
        {expandedPost && (
          <motion.div
            className={styles.postModalOverlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setExpandedPost(null)}
          >
            <motion.div
              className={styles.postModalCard}
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className={styles.postModalClose}
                onClick={() => setExpandedPost(null)}
                aria-label={t("close")}
              >
                <MdClose />
              </button>
              {expandedPost.image && (
                <img src={expandedPost.image} alt={expandedPost.title} />
              )}
              <div className={styles.postModalBody}>
                <h4>{expandedPost.title}</h4>
                <p>{expandedPost.content}</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
export default Home;
